import { get, difference } from 'lodash';
import {
  squareMetersToSquareFeet,
  squaredInchesToSquaredFeet,
  squaredInchesToSquaredMeters,
} from 'src/lib/conversionUtils';
import constants from 'src/constants';
import featureFlags from 'src/featureFlags';

const { DOOR_PACKAGE, SASH_ID, OPENINGS_TRIM_ID } = constants.labels;

export default class SurfaceAreaItems {
  constructor(labels, labelAreas, elements, isRoofOnly, useWindowConsistentMeasurements) {
    this.labels = labels;
    this.labelAreas = labelAreas;
    this.isRoofOnly = isRoofOnly;
    this.areaType = 'facadesMinusAnnotations';
    this.labelIdDelimiter = ':';
    this.labelIds = this.extractLabelIds(labelAreas);
    this.elements = elements;
    this.countAndAreaOfElementsByLabel = this.getCountAndAreaOfElementsByLabel(true);
    this.useWindowConsistentMeasurements = useWindowConsistentMeasurements;
  }

  // Private Methods

  getMacheteArea(element, rounded) {
    const { areaMinusAnnotations: elementAreaMeters } = element;
    const elementAreaFeet = squareMetersToSquareFeet(elementAreaMeters);
    return {
      meters: rounded ? Math.round(elementAreaMeters) : elementAreaMeters,
      feet: rounded ? Math.round(elementAreaFeet) : elementAreaFeet,
    };
  }

  getManowarArea(element, rounded) {
    const { manowarData: { area } } = element;
    const elementAreaFeet = squaredInchesToSquaredFeet(area);
    const elementAreaMeters = squaredInchesToSquaredMeters(area);
    return {
      meters: rounded ? Math.round(elementAreaMeters) : elementAreaMeters,
      feet: rounded ? Math.round(elementAreaFeet) : elementAreaFeet,
    }
  }

  getCountAndAreaOfElementsByLabel(rounded = false) {
    return this.elements.reduce((agg, element) => {
      this.labelIds.forEach((labelId) => {
        const labelIds = labelId
          .split(this.labelIdDelimiter)
          .map((id) => Number(id));
        if (element.containsLabels(labelIds)) {
          const area =
            (labelIds.includes(SASH_ID) || labelIds.includes(OPENINGS_TRIM_ID)) &&
              featureFlags.shouldUseWindowConsistentMeasurements() && this.useWindowConsistentMeasurements
              ? this.getManowarArea(element, rounded)
              : this.getMacheteArea(element, rounded);
          if (agg[labelId] === undefined) {
            agg[labelId] = { count: 1, area };
          } else {
            agg[labelId] = {
              count: agg[labelId].count + 1,
              area: {
                meters: agg[labelId].area.meters + area.meters,
                feet: agg[labelId].area.feet + area.feet,
              }
            };
          }
        }
      });
      return agg;
    }, {});
  }

  isCombinationLabel(ids) {
    return ids.includes(this.labelIdDelimiter);
  }

  isDoorPackage(ids) {
    return ids.includes(DOOR_PACKAGE.toString());
  }

  extractLabelIds(labelAreas) {
    let labelIds = Object.keys(labelAreas).filter((ids) =>
      this.isCombinationLabel(ids),
    );
    if (this.isRoofOnly) {
      labelIds = labelIds.filter((ids) => !this.isDoorPackage(ids));
    }
    return labelIds;
  }

  /**
   * Check if a label is a color. A label beginning with
   * 3 or 5 is a color (e.g. 301, 511).
   * @param {string} labelId
   * @returns {boolean}
   * @memberof SurfaceAreaItems
   */
  isLabelColor(labelId) {
    if (!labelId) return false;
    return /^[35]/.test(labelId.toString());
  }

  /**
   * Get the display name from an array of labelIds.
   * @param {array} labelIds
   * @returns {string}
   * @memberof SurfaceAreaItems
   */
  getDisplayName(labelIds) {
    return labelIds
      .reduce((displayName, id) => {
        const label = this.labels[id];
        if (label) {
          if (this.isLabelColor(id)) {
            displayName.unshift(label.name);
          } else {
            displayName.push(label.name);
          }
        }
        return displayName;
      }, [])
      .join(' ');
  }

  getThumbnailUrls(labelIds) {
    return labelIds.reduce((thumbnailUrls, id) => {
      const thumbnailUrl = get(this.labels, [id, 'texture', 'url']);
      if (thumbnailUrl) {
        thumbnailUrls.push(thumbnailUrl);
      }
      return thumbnailUrls;
    }, []);
  }

  getTotalAreas() {
    return this.labelIds.reduce((sum, id) => {
      const { area: labelArea } = this.countAndAreaOfElementsByLabel[id];
      return {
        meters: sum.meters + labelArea.meters,
        feet: sum.feet + labelArea.feet,
      };
    }, { meters: 0, feet: 0 });
  }

  sortItemsByArea(items) {
    return items.sort((a, b) => get(b, 'area.meters') - get(a, 'area.meters'));
  }

  getCountOfElements(id) {
    return this.countAndAreaOfElementsByLabel[id].count;
  }

  getTotalCountOfElements() {
    return Object.keys(this.countAndAreaOfElementsByLabel).reduce((sum, labelId) => {
      sum += this.countAndAreaOfElementsByLabel[labelId].count;
      return sum;
    }, 0);
  }

  getUuids(labelIds) {
    const labelIdsNums = labelIds.map(Number);
    return this.elements
      .filter(
        (element) => difference(labelIdsNums, element.labels).length === 0,
      )
      .map((element) => element.uuid);
  }

  generateSurfaceAreaItems() {
    const items = this.labelIds.map((id) => {
      const labelIds = id.split(this.labelIdDelimiter);

      return {
        labelIds: id.split(this.labelIdDelimiter),
        uuids: this.getUuids(labelIds),
        displayName: this.getDisplayName(labelIds),
        thumbnailUrls: this.getThumbnailUrls(labelIds),
        area: this.countAndAreaOfElementsByLabel[id].area,
        count: this.countAndAreaOfElementsByLabel[id].count,
      };
    });

    return this.sortItemsByArea(items);
  }

  getTotals() {
    return {
      area: this.getTotalAreas(),
      count: this.getTotalCountOfElements(),
    };
  }

  // Public Methods
  getData() {
    return {
      items: this.generateSurfaceAreaItems(),
      total: this.getTotals(),
    };
  }
}
